import React, { FC, ReactElement } from 'react';
import { ToastContainer, toast, Id } from 'react-toastify';
import { Theme, useTheme as useMuiTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { errors } from '@lecab/errors';
import * as Sentry from '@sentry/react';
// Toastify
import 'react-toastify/dist/ReactToastify.css';
// Components
import Text from '../../components/text';
import SvgIcon from '../../components/icons/SvgIcon';
// Resources
import { language } from '../../resources/strings';
import { IconAxel, IconCommon } from '../../resources/images';
import appConfig from '../../config/appConfig';
import { getTopBarHeight } from '../../localStorageAPI';
import { isWebView } from '../../utils/webview';
import { TOAST_DEDUPE_TIMEOUT } from '../../utils/constants';
import configs from '../../config';

interface LastToast {
    message: string;
    type: ToastType;
    timestamp: number;
}

const ToastProvider: FC = (): ReactElement => {
    const classes = useStyles({
        isWebView: isWebView(),
        statusBarHeight: getTopBarHeight() || 0,
    });

    return (
        <ToastContainer
            autoClose={5000}
            hideProgressBar
            newestOnTop
            closeOnClick
            closeButton={false}
            theme="colored"
            className={classes.toastProviderContainer}
            position="top-center"
            draggableDirection="y"
            draggablePercent={50}
        />
    );
};

// The surface a toast sits on: white in light mode, the dark surface grey in dark mode
const toastSurface = (theme: Theme): string => theme.palette.colors.background.appBar;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        toastProviderContainer: (props: { isWebView: boolean; statusBarHeight: number }) => ({
            // Every toast surface is the app bar surface tinted by type: white in light mode, dark grey in dark mode.
            // A literal #FFFFFF base here left every toast white while the text follows the theme, so dark mode
            // rendered near-white text on white.
            '--toastify-color-info': `linear-gradient(0deg, ${theme.palette.colors.border}, ${theme.palette.colors.border}), linear-gradient(0deg, rgba(69, 112, 234, 0.2), rgba(69, 112, 234, 0.2)), ${toastSurface(theme)}`,
            '--toastify-color-success': `linear-gradient(0deg, rgba(0, 220, 0, 0.12), rgba(0, 220, 0, 0.12)), ${toastSurface(theme)}`,
            // '--toastify-color-warning': '#f1c40f',
            '--toastify-color-error': `linear-gradient(0deg, rgba(220, 0, 0, 0.12), rgba(220, 0, 0, 0.12)), ${toastSurface(theme)}`,
            '--toastify-color-dark': toastSurface(theme),
            // Used by the untyped and `loading` toasts, which react-toastify renders as its "default" type
            '--toastify-color-light': toastSurface(theme),

            '--toastify-text-color-info': theme.palette.secondary.light,
            '--toastify-text-color-success': theme.palette.colors.text.success,
            // '--toastify-text-color-warning': '#fff',
            '--toastify-text-color-error': theme.palette.colors.text.error,
            '--toastify-text-color-dark': theme.palette.primary.main,
            '--toastify-text-color-light': theme.palette.primary.main,

            '--toastify-spinner-color': theme.palette.colors.text.label,
            '--toastify-spinner-color-empty-area': theme.palette.colors.background.selected,

            '--toastify-toast-width': 'auto',
            '--toastify-toast-min-height': 52,
            '--toastify-font-family': 'Madras',

            '&.Toastify__toast-container--top-center': {
                top: `${props.statusBarHeight}px`,
            },
            '&.Toastify__toast-container': {
                fontSize: 16,
                fontWeight: 900,
                padding: props.isWebView ? 20 : 0,
            },
            '& .Toastify__toast-body': {
                padding: '8px 16px',
            },
            '& .Toastify__toast': {
                borderRadius: 10,
            },
            '& .Toastify__toast-icon': {
                marginInlineEnd: 12,
            },

            '& [class*="LC_"]': {
                color: theme.palette.primary.main,
                background: toastSurface(theme),
                '& .Toastify__toast-icon': {
                    width: 'unset',
                    padding: 8,
                    backgroundColor: theme.palette.colors.background.lightGreen,
                    borderRadius: 4,
                },
                '&.LC_error': {
                    color: theme.palette.colors.text.error,
                },
                '&.LC_error .Toastify__toast-icon': {
                    backgroundColor: `${theme.palette.colors.text.error}29`,
                },
                // '&.LC_warn, &.LC_warning': {
                //     color: theme.palette.colors.text.warning,
                // },
                '&.LC_warn .Toastify__toast-icon, &.LC_warning .Toastify__toast-icon': {
                    backgroundColor: `${theme.palette.colors.text.warning}29`,
                },
                // '&.LC_info': {
                //     color: theme.palette.secondary.light,
                // },
                '&.LC_info .Toastify__toast-icon': {
                    backgroundColor: `${theme.palette.colors.text.success}29`, // green
                    // backgroundColor: `${theme.palette.secondary.light}29`, // blue
                },
            },
        }),
    }),
);

// Rendered inside the ToastContainer, so it can read the palette that the imperative toast() helpers cannot
const ToastLeadingIcon: FC<{ icon: IconCommon | IconAxel; themed: boolean }> = ({ icon, themed }): ReactElement => {
    const theme = useMuiTheme();

    if (!themed) {
        return <SvgIcon icon={icon} stroke="currentColor" />;
    }

    const iconColor = theme.palette.colors.icon.primary;

    return <SvgIcon icon={icon} stroke={iconColor} />;
};

export type ToastType = 'loading' | 'success' | 'info' | 'error' | 'warning' | 'warn' | 'dark';

let lastToastRef: LastToast | null = null;

const isDuplicateToast = (message: string, type: ToastType): boolean => {
    if (!lastToastRef) {
        return false;
    }

    const now = Date.now();
    return (
        lastToastRef.message === message &&
        lastToastRef.type === type &&
        now - lastToastRef.timestamp < TOAST_DEDUPE_TIMEOUT
    );
};

export const ToastWithIcon = (type: ToastType, label: string, icon?: IconCommon | IconAxel, title?: string): Id => {
    // Check for duplicate toast
    if (isDuplicateToast(label, type)) {
        return -1;
    }

    // Determine style based on presence of title or label
    const newStyle = title || label;

    // Update last toast reference
    lastToastRef = {
        message: label,
        type,
        timestamp: Date.now(),
    };

    let useIcon = icon;
    if (!useIcon) {
        switch (type) {
            case 'error':
                useIcon = IconCommon.InformationOutline;
                break;
            case 'info':
                useIcon = IconCommon.Check;
                break;
            default:
                useIcon = IconCommon.Check;
        }
    }

    const content = newStyle ? (
        <div
            style={{
                gap: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <div>
                <Text.ShortMediumText14 text={title || label} />
                {title && label && <Text.ShortRegularText14 text={label} />}
            </div>
            <SvgIcon icon={IconCommon.Close} color="black" fix={20} />
        </div>
    ) : (
        label
    );

    return toast[type](content, {
        icon: <ToastLeadingIcon icon={useIcon} themed={Boolean(newStyle)} />,
        className: newStyle ? `LC_${type}` : '',
    });
};

export const ToastWithError = (error: Error, defaultMessage = ''): Id => {
    // Discard toast for duplicated request
    if (+error?.cause === 430) {
        return -1;
    }
    const { errorCode } = (error as any)?.response?.data || {};
    if (appConfig.useSentry && !errorCode) {
        Sentry.captureException(error);
    }
    const errorCab = new errors.ClientError(errorCode);
    // Without an errorCode the SDK still answers with its generic "unknown error" message, so the caller's
    // defaultMessage (which says *which* action failed) is the better one to show.
    const genericMessage = errorCab.clientMessage[language];
    let errorMessage = (errorCode ? genericMessage : defaultMessage) || genericMessage;
    if (configs.envConfig.currentEnv.isDev || (!errorCode && !defaultMessage)) {
        errorMessage = `${error?.message ?? ''} - ${errorMessage}`;
    }

    return ToastWithIcon('error', errorMessage);
};

export default ToastProvider;
